import { useState, type ReactNode } from 'react';
import type { DocumentEntity, LocationNode, MeasurementSeries } from '@oscom/shared';
import { createDocument } from '../api/documents';
import {
  DocumentFields,
  defaultSiteLocationId,
  documentFieldsComplete,
  type DocumentFieldValues,
} from './DocumentFields';

/** "Neues Dokument": template, title, location and series; then the editor opens. */
export function DocumentCreateDialog({
  customerId,
  locations,
  series,
  onDone,
  onCancel,
}: {
  customerId: string;
  locations: LocationNode[];
  series: MeasurementSeries[];
  onDone: (document: DocumentEntity) => void;
  onCancel: () => void;
}): ReactNode {
  const [values, setValues] = useState<DocumentFieldValues>(() => ({
    documentTypeKey: 'beacon-gsm-concept',
    title: '',
    subtitle: '',
    siteLocationId: defaultSiteLocationId(locations),
    seriesIds: [],
  }));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const complete = documentFieldsComplete(values);

  async function handleSubmit(): Promise<void> {
    if (!complete || submitting) return;
    setSubmitting(true);
    setError(undefined);
    try {
      const subtitle = values.subtitle.trim();
      const document = await createDocument(customerId, {
        documentTypeKey: values.documentTypeKey,
        title: values.title.trim(),
        ...(subtitle ? { subtitle } : {}),
        siteLocationId: values.siteLocationId,
        seriesIds: values.seriesIds,
      });
      onDone(document);
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : 'Anlegen fehlgeschlagen.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="card" role="dialog" aria-modal="false" aria-labelledby="document-create-title">
      <h2 id="document-create-title">Neues Dokument</h2>
      <form
        className="stack"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        <DocumentFields
          values={values}
          onChange={setValues}
          locations={locations}
          series={series}
          templateFixed={false}
        />
        {error ? (
          <p className="notice notice--warning notice--multiline" role="alert">
            {error}
          </p>
        ) : null}
        <div className="plan-info__actions">
          <button
            type="submit"
            className="button button--primary"
            disabled={!complete || submitting}
          >
            Anlegen
          </button>
          <button type="button" className="button" onClick={onCancel}>
            Abbrechen
          </button>
        </div>
      </form>
    </div>
  );
}
